// src/components/landing/HowItWorks.jsx
import React, { useEffect, useRef } from 'react';
import { useReveal, prefersReducedMotion } from '../../utils/reveal';
import {
  IconPlot, IconCompliance, IconDesign, IconCorrect, IconExtrude, IconScale,
} from './LandingIcons';

const STAGES = [
  { icon: IconPlot, title: 'Establish the site',
    text: 'Trace the boundary on a map (or draw it); area, perimeter, edges, context and terrain come with it.' },
  { icon: IconCompliance, title: 'Resolve what may be built',
    text: 'The regulation is resolved from the plot and applied as setbacks, FSI, coverage and height; a massing is generated inside the envelope.' },
  { icon: IconDesign, title: 'Read the drawings you already have',
    text: 'A PDF or a photo is read into measurable rooms and openings — nothing to redraw first.' },
  { icon: IconCorrect, title: 'Correct what was read',
    text: 'Retype, resize, split, merge or draw a missed room; every edit is validated and saved against the drawing.' },
  { icon: IconExtrude, title: 'Extrude, place and check',
    text: 'Rooms rise floor by floor onto the site and rebuild live as you edit; take dimensions off anything.' },
  { icon: IconScale, title: 'Read it against law and tradition',
    text: "Compliance, climate, cost and a sourced Vastu reading, each grounded in this project's own data." },
];

/** One stage. Observed on its own, so on a phone each node pops as IT is reached. */
function Stage({ stage, index }) {
  const ref = useReveal();
  const Icon = stage.icon;
  return (
    <li ref={ref} className="lp-step" style={{ '--i': index }}>
      <span className="lp-step-node" aria-hidden="true"><Icon /></span>
      <div className="lp-step-body">
        <span className="lp-step-num">Stage {index + 1}</span>
        <h3 className="lp-h3">{stage.title}</h3>
        <p className="lp-step-text">{stage.text}</p>
      </div>
    </li>
  );
}

export default function HowItWorks() {
  const wrapRef = useRef(null);

  // The connecting line follows the scroll: its scale is how far the list has
  // travelled through the viewport, written as --p. A layout READ then a style
  // WRITE inside one animation frame, so it never forces a second layout.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    if (prefersReducedMotion()) {
      el.style.setProperty('--p', '1');
      return undefined;
    }
    let raf = 0;
    const update = () => {
      raf = 0;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const start = vh * 0.85;
      const end = vh * 0.4;
      const p = (start - r.top) / (r.height + start - end);
      el.style.setProperty('--p', String(Math.max(0, Math.min(1, p))));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section id="how" className="lp-section" aria-labelledby="lp-how-title">
      <div className="lp-wrap">
        <h2 id="lp-how-title" className="lp-h2">How it works</h2>
        <div ref={wrapRef} className="lp-steps-wrap">
          <span className="lp-steps-line" aria-hidden="true" />
          <ol className="lp-steps">
            {STAGES.map((s, i) => <Stage key={s.title} stage={s} index={i} />)}
          </ol>
        </div>
      </div>
    </section>
  );
}
